import {
    Coverage,
    Insurance,
    Office,
} from '@/core/domain/extras/models/extras-data.model';
import { isValid } from '@/common/utils/validation/common.utils';
import { CdnService } from '@/common/services/cdn/cdn.service';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

@Component({
    selector: 'fe-protection-level',
    templateUrl: './protection-level.component.html',
    styleUrls: ['./protection-level.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ProtectionLevelComponent implements OnInit {
    @Input() insurances!: Insurance[];
    @Input() currency?: string;
    @Input() rentDays?: number;
    @Input() freeDays?: number;
    @Input() disabled?: boolean;
    @Output() activeInsurancesChange = new EventEmitter<Insurance[]>();

    hasCoupon: string | boolean = false;
    isInternational = false;
    isOffice = true;
    isActive: boolean[] = [];
    isActiveDetails: boolean[] = [];

    // Íconos y taglines por id de protección. Las coberturas de cada tarjeta
    // vienen del backend ya traducidas en `coverages`.
    cardMeta = [
        {
            id: 5,
            icon: 'images/insurance-premium.svg',
            iconActive: 'images/premium-select.svg',
            tagline: 'fe_details_step3_protection_level_big_protection_card_title',
            ctaKey: 'fe_details_step3_protection_level_protection_card_cta_premium',
            ctaSelectedKey:
                'fe_details_step3_protection_level_protection_card_cta_premium_selected',
        },
        {
            id: 6,
            icon: 'images/insurance-tall.svg',
            iconActive: 'images/amplia-select.svg',
            tagline: 'fe_details_step3_protection_level_medium_protection_card_title',
            ctaKey: 'fe_details_step3_protection_level_protection_card_cta_amplia',
            ctaSelectedKey:
                'fe_details_step3_protection_level_protection_card_cta_amplia_selected',
        },
        {
            id: 7,
            icon: 'images/insurance-low.svg',
            iconActive: 'images/basica-select.svg',
            tagline: 'fe_details_step3_protection_level_minimal_protection_card_title',
            ctaKey: 'fe_details_step3_protection_level_protection_card_cta_basica',
            ctaSelectedKey:
                'fe_details_step3_protection_level_protection_card_cta_basica_selected',
        },
    ];

    private readonly recommendedInsuranceId = 6;

    constructor(private store: Store) {}

    get recommendedIndex(): number {
        const index = this.insurances?.findIndex(
            (insurance) => insurance.id === this.recommendedInsuranceId,
        );
        return index >= 0 ? index : 0;
    }

    ngOnInit(): void {
        this.isActive = new Array(this.insurances.length).fill(false);
        this.isActiveDetails = new Array(this.insurances.length).fill(false);

        this.insurances.forEach((insurance, index) => {
            if (insurance.required === true) {
                this.toggleActive(insurance, index, true);
            }
        });

        this.store
            .select(selectReservation)
            .pipe(take(1))
            .subscribe((reservation) => {
                if (reservation?.discountCode !== null) {
                    this.hasCoupon = reservation.discountCode ?? false;
                }
                this.isInternational = reservation.origin === 'INT';
                if (reservation?.flowType !== 'office') {
                    this.isOffice = false;
                }
                if (
                    reservation?.insurancesSessionId &&
                    reservation.insurancesSessionId === reservation.uuid
                ) {
                    this.restoreSelection(reservation.insurances);
                }
            });
    }

    private restoreSelection(selected?: Insurance[]): void {
        if (!selected?.length) {
            return;
        }

        const selectedIds = selected.map((insurance) => insurance.id);
        this.insurances.forEach((insurance, index) => {
            if (!this.isActive[index] && selectedIds.includes(insurance.id)) {
                this.toggleActive(insurance, index, true);
            }
        });
    }

    toggleActive(insuranceItem: Insurance, index: number, force = false): void {
        if (insuranceItem.required === true && !force) {
            return;
        }

        if (insuranceItem.location === 'INT') {
            this.isActive[index] = !this.isActive[index];
        } else if (this.isActive[index] && !force) {
            this.isActive[index] = false;
        } else {
            this.isActive = this.isActive.map((_, i) => i === index);
        }

        const activeInsurances = this.insurances.filter(
            (insurance, i) => this.isActive[i],
        );

        this.activeInsurancesChange.emit(activeInsurances);
    }

    onCardClick(insuranceItem: Insurance, index: number): void {
        if (this.disabled) {
            return;
        }
        this.toggleActive(insuranceItem, index);
    }

    toggleDetails(index: number): void {
        const open = !this.isActiveDetails[index];
        this.isActiveDetails = this.isActiveDetails.map(() => false);
        this.isActiveDetails[index] = open;
    }

    closeDetails(index: number): void {
        this.isActiveDetails[index] = false;
    }

    get hasFreeDays(): boolean {
        return !this.isInternational && !!this.freeDays;
    }

    get freeDaysKey(): string {
        return this.freeDays === 1
            ? 'fe_details_step3_protection_level_protection_title_free_days_singular'
            : 'fe_details_step3_protection_level_protection_title_free_days_plural';
    }

    get totalDaysKey(): string {
        return this.rentDays === 1
            ? 'fe_details_step3_protection_level_protection_card_total_day'
            : 'fe_details_step3_protection_level_protection_card_total_days';
    }

    getMeta(insurance: Insurance) {
        const meta =
            this.cardMeta.find((meta) => meta.id === insurance.id) ??
            this.cardMeta[this.cardMeta.length - 1];
        return {
            ...meta,
            icon: CdnService.url(meta.icon),
            iconActive: CdnService.url(meta.iconActive),
        };
    }

    getCtaKey(insurance: Insurance, index: number): string {
        if (this.isInternational) {
            return this.isActive[index]
                ? 'fe_details_step3_protection_level_protection_card_right_button_info_inactive'
                : 'fe_details_step3_protection_level_protection_card_right_button_info_active';
        }

        const meta = this.getMeta(insurance);
        return this.isActive[index] ? meta.ctaSelectedKey : meta.ctaKey;
    }

    getIcon(insurance: Insurance, index: number): string {
        const meta = this.getMeta(insurance);
        return !this.isInternational && this.isActive[index]
            ? meta.iconActive
            : meta.icon;
    }

    private getMethod(insurance: Insurance): Office | undefined {
        return this.isOffice ? insurance.office : insurance.prePaid;
    }

    getPrice(insurance: Insurance): number {
        return this.getMethod(insurance)?.price ?? insurance.price;
    }

    getDiscount(insurance: Insurance): string | undefined {
        return this.getMethod(insurance)?.discount;
    }

    getTotal(insurance: Insurance): number | undefined {
        const price = this.getPrice(insurance);

        if (!isValid(this.rentDays) || !isValid(price)) {
            return this.getMethod(insurance)?.totalPrice ?? insurance.totalPrice;
        }

        return Math.ceil(price) * (this.rentDays as number);
    }

    getSavings(insurance: Insurance): number | undefined {
        const method = this.isOffice ? insurance.office : insurance.prePaid;
        const savings = method?.totalDiscount;
        return savings !== undefined && savings > 0 ? savings : undefined;
    }

    getFeatures(insurance: Insurance): Coverage[] {
        return [...(insurance.coverages ?? [])].sort(
            (a, b) => a.orden - b.orden,
        );
    }

    isHighlightedFeature(feature: Coverage): boolean {
        return Number(feature.orden) === 20;
    }
}
